import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { addToCart } from '../redux/healthcareCartSlice';
import placeholderImage from "../assets/img/menuImage.png";
import ProductImageGallery from "./ProductImageGallery";
import useCoverage from '../redux/useCoverage';
import { formatCurrency, SUPPORT_PHONE } from '../config/market';
import DealTag from './DealTag';
import PriceCut from './PriceCut';
import { hasSalePrice, unitPriceOf, unitSavingsOf } from '../utils/vendorDiscounts';
/* This dialog was completely blind to price-on-request: it printed a "Base
   Price" straight off `product.price` and gated Add to Cart on the delivery zone
   alone, so a quote-only pharmacy line showed a number the server would never
   charge (or a confident zero once the server started nulling it) above a live
   button. Same helper as the brand storefront, search, the share text and both
   customer apps. */
import {
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
  isQuoteOnly,
  publishedPrice,
} from '../utils/priceOnRequest';
import { buildVendorWhatsAppHref } from './VendorWhatsAppFloat';
/* Branch operations (4 Oct 2026): the product as it stands at this pharmacy
   branch, and one branch per cart. */
import OutletSwitchDialog from './OutletSwitchDialog';
import { addButtonLabel, blockedHere, locationKeyOf } from '../utils/branchAvailability';
import BranchAvailabilityBadge from './branch/BranchAvailabilityBadge';
import { cartLocationOf } from '../utils/branchCart';

const HealthcareProductDialog = ({
  product,
  onClose,
  vendorId,
  vendor,
  // The pharmacy branch the storefront is open on (null for the pharmacy
  // itself), its name, the name of the branch the cart is already on, and
  // what the page does once the cart has moved (the cart check there).
  outletId = null,
  branchName = '',
  cartBranchName = '',
  onCartMoved,
  // Every place the pharmacy sells from, and the page's "Get it from" (it
  // closes this dialog and opens that branch, asking first about a cart from
  // another branch), as on the apps' product page (4 Oct 2026).
  locations = null,
  onGetItFrom,
}) => {
  const dispatch = useDispatch();
  const cart = useSelector((state) => (vendorId ? state.healthcareCart.carts[vendorId] : null)) || null;
  // The add waiting on "Move your cart to <branch>?", or null.
  const [pendingMove, setPendingMove] = useState(null);
  /* The bound the gallery measures its magnified panel against. Inside a
     dialog the stage spans the whole paper and there is no column to its
     right, so without this the panel was positioned off the end of the
     dialog and drawn, at z-index 9999, over its own dimmed backdrop. Given
     the bound the gallery works out there is no room and shows the lens
     nowhere, leaving click to enlarge, which a modal can do properly. */
  const detailColRef = useRef(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState([]);

  // AUTHORITATIVE deliverability (GET /api/coverage — same road-distance +
  // radius verdict as the order-create 422 gate; cache is pre-warmed by the
  // shop page's own useCoverage call, so this normally costs no request).
  // Replaces the old client-side haversine check, which hardcoded a 5 km
  // default radius and could disagree with checkout.
  const coverage = useCoverage({
    vendorId,
    outletId,
    vendorType: 'healthcare',
    enabled: Boolean(vendorId),
  });

  if (!product) return null;

  /* THE POSTURE, once, so the price row, the total and the button cannot
     disagree. Three states, not two: quote-only is a deliberate commercial
     posture (pharmacies sell in bulk as well as at retail, so it is ordinary
     here), unpriced is a SKU nobody has costed yet, and only the third has a
     number to show. Never inferred from `price === 0`: a quote-only item is
     explicitly allowed to keep a price from before the pharmacy flipped the
     posture, and an unpriced import is not a quote item. */
  const quoteOnly = isQuoteOnly(product);
  const shownPrice = publishedPrice(product);
  const unpriced = !quoteOnly && shownPrice === null;
  const hasNoOrderablePrice = quoteOnly || unpriced;

  const isOutsideDeliveryZone = coverage.isOutOfRange;
  const coverageKm = Number.isFinite(Number(coverage.distanceKm))
    ? Number(coverage.distanceKm).toFixed(1)
    : null;

  /* NO IN-PLATFORM HEALTHCARE QUOTE FLOW EXISTS YET. The quote-request
     endpoints are brand-scoped (POST /api/brands/:id/quote-requests, and My
     Quotes reads brand quotes), so there is nothing here to post a pharmacy
     quote to and fabricating a call would fail silently in front of the
     shopper. Until the healthcare quote flow lands, a quote-only line sends the
     shopper to the pharmacy on the contact details this dialog already holds.
     WHEN THAT FLOW LANDS: replace this block with the real request, the same way
     BrandProductPage routes to the brand shop's quote modal, and keep the
     wording ("Price on request") identical so nothing has to be relearned. */
  const vendorName = vendor?.tradingName || vendor?.name || 'this pharmacy';
  const vendorPhone = vendor?.phone || '';
  const vendorEmail = vendor?.email || '';
  const quoteEnquiryText = [
    `Hi ${vendorName}, I found you on Lusuku.`,
    `Please quote me for: ${product.name}${product.skuCode ? ` (${product.skuCode})` : ''}.`,
    `Quantity needed: ${quantity}.`,
  ].join('\n');
  const quoteWhatsAppHref = buildVendorWhatsAppHref({
    phone: vendorPhone,
    vendorName,
    vendorType: 'healthcare',
    message: quoteEnquiryText,
  });

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1) {
      if (product.productType === 'PRODUCT' && newQuantity <= product.countInStock) {
        setQuantity(newQuantity);
      } else if (product.productType === 'SERVICE' && newQuantity <= product.availableSlots) {
        setQuantity(newQuantity);
      } else if (product.productType === 'SERVICE') {
        setQuantity(newQuantity);
      }
    }
  };

  const handleOptionToggle = (group, option) => {
    const existing = selectedOptions.find(
      (opt) => opt.groupId === group._id && opt.optionId === option._id
    );

    if (existing) {
      setSelectedOptions(
        selectedOptions.filter((opt) => opt.optionId !== option._id)
      );
    } else {
      if (group.required || !group.multipleSelection) {
        setSelectedOptions([
          ...selectedOptions.filter((opt) => opt.groupId !== group._id),
          {
            groupId: group._id,
            optionId: option._id,
            name: option.name,
            price: option.price || 0,
          },
        ]);
      } else {
        setSelectedOptions([
          ...selectedOptions,
          {
            groupId: group._id,
            optionId: option._id,
            name: option.name,
            price: option.price || 0,
          },
        ]);
      }
    }
  };

  const isOptionSelected = (groupId, optionId) => {
    return selectedOptions.some(
      (opt) => opt.groupId === groupId && opt.optionId === optionId
    );
  };

  const calculateTotalPrice = () => {
    // The sale price while an offer is live: the number the server charges.
    const basePrice = unitPriceOf(product) * quantity;
    const optionsPrice = selectedOptions.reduce(
      (sum, opt) => sum + opt.price * quantity,
      0
    );
    return basePrice + optionsPrice;
  };

  const applyAdd = (payload, { moved = false } = {}) => {
    dispatch(addToCart(payload));
    if (moved && typeof onCartMoved === 'function') onCartMoved();
    onClose();
  };

  const handleAddToCart = () => {
    const payload = {
      vendorId,
      product: {
        ...product,
        healthcareId: product.healthcareId,
        sectionId: product.sectionId,
        requiresPrescription: product.requiresPrescription,
        ageRestriction: product.ageRestriction,
        controlledSubstance: product.controlledSubstance,
        productType: product.productType,
      },
      quantity,
      options: selectedOptions,
      // The branch, named outright (null is the pharmacy itself): one
      // pharmacy cart holds ONE branch (4 Oct 2026).
      outletId: outletId || null,
    };
    /* A cart already on another branch of this pharmacy asks first; on a yes
       the whole cart moves here and is checked at this branch. */
    const cartElsewhere =
      Object.keys(cart || {}).length > 0 &&
      cartLocationOf(cart) !== locationKeyOf(outletId || null);
    if (cartElsewhere) {
      setPendingMove(payload);
      return;
    }
    applyAdd(payload);
  };

  // Sold out or switched off at this branch (4 Oct 2026).
  const branchBlocked = !hasNoOrderablePrice && blockedHere(product);

  const canAddToCart = () => {
    /* Posture first. A line with no chargeable price is refused by the server
       whatever this dialog believes (PRICE_ON_REQUEST or PRICE_UNSET), so an
       enabled button here could only ever produce a failed checkout. */
    if (hasNoOrderablePrice || branchBlocked) {
      return false;
    }
    // Check if outside delivery zone
    if (isOutsideDeliveryZone) {
      return false;
    }
    
    if (product.productType === 'PRODUCT' && product.countInStock <= 0) {
      return false;
    }
    if (product.productType === 'SERVICE' && product.availableSlots <= 0) {
      return false;
    }
    
    // Check if all required option groups have selections
    if (product.optionGroups && product.optionGroups.length > 0) {
      return product.optionGroups.every((group) => {
        if (group.required) {
          return selectedOptions.some((opt) => opt.groupId === group._id);
        }
        return true;
      });
    }
    return true;
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleStyled>
        <Typography variant="h6" component="div" style={{ flex: 1 }}>
          {product.name}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitleStyled>
      
      <DialogContentStyled ref={detailColRef}>
        {/* A pharmacy line is a product like any other, so it gets the SAME
            viewer a brand product gets: the rail, the hover lens with its
            magnified panel, click to enlarge, keyboard arrows and video
            tiles. It used to render a bare MagnifiableImage inside a fixed
            380px box, which was the weakest picture on the platform in the
            one vertical where a shopper most needs to read a label: dosage,
            strength, expiry, the small print on a box. No renderMain here on
            purpose, so the stage is the shared one and healthcare inherits
            every later improvement to it for free. */}
        <ProductImageContainer>
          <ProductImageGallery
            images={[
              product.image,
              ...(Array.isArray(product.additionalImages)
                ? product.additionalImages
                : []),
            ]}
            videos={product.videos}
            alt={product.name}
            placeholder={placeholderImage}
            detailRef={detailColRef}
            hoverZoom
            lightbox
            mainHeight="380px"
            mobileMainHeight="250px"
            /* 768, not the component's 640 default, and that is the whole
               point of passing them. This dialog has exactly ONE breakpoint
               of its own, 768, and the box these two heights replaced
               switched from 380 to 250 there. Inheriting the default left a
               641 to 768 band (iPad portrait, a half-width laptop window, a
               phone in landscape) where the stage stayed tall and the rail
               stayed a narrow column beside it, inside a dialog that had
               already narrowed. The gallery's own header warns about exactly
               that: a rail still beside the stage after the surface has gone
               single column is worse than no gallery at all. */
            railBreakpoint={768}
            stageHeightBreakpoint={768}
            /* Half the platform's stage padding. 20px top and bottom is a
               tenth of this 380px stage (and a sixth of the 250px phone
               one), and a pharmacy shopper is here to read a label:
               dosage, strength, expiry, the small print on the box. */
            stagePadding="10px"
            overlay={
              hasSalePrice(product) ? (
                <DealTag
                  discount={product.discount}
                  size="l"
                  radius={8}
                  zIndex={2}
                />
              ) : null
            }
          />
        </ProductImageContainer>

        {/* Healthcare Badges */}
        <BadgesContainer>
          {product.requiresPrescription && (
            <AlertBadge severity="error">
              <WarningIcon fontSize="small" />
              <span>℞ Prescription Required</span>
            </AlertBadge>
          )}
          {product.ageRestriction > 0 && (
            <AlertBadge severity="warning">
              <WarningIcon fontSize="small" />
              <span>Age {product.ageRestriction}+ Restriction</span>
            </AlertBadge>
          )}
          {product.controlledSubstance && (
            <AlertBadge severity="error">
              <WarningIcon fontSize="small" />
              <span>Controlled Substance</span>
            </AlertBadge>
          )}
          {product.productType === 'SERVICE' && (
            <ServiceBadge>
              <MedicalServicesIcon fontSize="small" />
              <span>Medical Service</span>
            </ServiceBadge>
          )}
        </BadgesContainer>

        {product.description && (
          <DescriptionSection>
            <SectionTitle>Description</SectionTitle>
            <Description>{product.description}</Description>
          </DescriptionSection>
        )}

        <PriceSection>
          <PriceLabel>{hasNoOrderablePrice ? 'Price' : 'Base Price'}</PriceLabel>
          {quoteOnly ? (
            <Price>{PRICE_ON_REQUEST_LABEL}</Price>
          ) : unpriced ? (
            <Price>{PRICE_UNAVAILABLE_LABEL}</Price>
          ) : (
            <PriceCut
              component={Price}
              price={shownPrice}
              listPrice={product.listPrice}
              salePrice={product.salePrice}
              format={formatCurrency}
              savings={unitSavingsOf(product)}
              endsAt={product.discount?.endsAt}
              align="end"
            />
          )}
        </PriceSection>

        {product.productType === 'PRODUCT' && (
          <StockInfo inStock={product.countInStock > 0}>
            {product.countInStock > 0 ? (
              <>
                <CheckCircleIcon fontSize="small" />
                <span>In Stock ({product.countInStock} available)</span>
              </>
            ) : (
              <span>Out of Stock</span>
            )}
          </StockInfo>
        )}

        {product.productType === 'SERVICE' && (
          <StockInfo inStock={product.availableSlots > 0}>
            <MedicalServicesIcon fontSize="small" />
            <span>
              {product.availableSlots > 0
                ? `${product.availableSlots} slots available`
                : 'No slots available'}
            </span>
          </StockInfo>
        )}

        {/* Option Groups */}
        {product.optionGroups && product.optionGroups.length > 0 && (
          <OptionsSection>
            {product.optionGroups.map((group) => (
              <OptionGroup key={group._id}>
                <OptionGroupHeader>
                  <OptionGroupTitle>
                    {group.name}
                    {group.required && <RequiredLabel>(Required)</RequiredLabel>}
                  </OptionGroupTitle>
                  {!group.multipleSelection && (
                    <OptionGroupSubtitle>Select one</OptionGroupSubtitle>
                  )}
                </OptionGroupHeader>
                <OptionsList>
                  {group.options.map((option) => (
                    <OptionButton
                      key={option._id}
                      onClick={() => handleOptionToggle(group, option)}
                      selected={isOptionSelected(group._id, option._id)}
                    >
                      <OptionContent>
                        <OptionName>{option.name}</OptionName>
                        {option.price > 0 && (
                          <OptionPrice>
                            +{formatCurrency(option.price)}
                          </OptionPrice>
                        )}
                      </OptionContent>
                      {isOptionSelected(group._id, option._id) && (
                        <CheckCircleIcon color="primary" fontSize="small" />
                      )}
                    </OptionButton>
                  ))}
                </OptionsList>
              </OptionGroup>
            ))}
          </OptionsSection>
        )}

        {/* An unpriced SKU gets no stepper: there is nothing to order and
            nothing to ask for. A quote-only one keeps it, because the number is
            what the pharmacy is being asked to quote against. */}
        {!unpriced && (
          <QuantityContainer>
            <QuantityLabel>
              {quoteOnly ? 'Quantity to quote' : 'Quantity'}
            </QuantityLabel>
            <QuantityControls>
              <QuantityButton
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
              >
                <RemoveIcon style={{ color: '#E91E63' }} />
              </QuantityButton>
              <QuantityDisplay>{quantity}</QuantityDisplay>
              <QuantityButton
                onClick={() => handleQuantityChange(1)}
                disabled={
                  product.productType === 'PRODUCT'
                    ? quantity >= product.countInStock
                    : product.productType === 'SERVICE'
                    ? quantity >= product.availableSlots
                    : false
                }
              >
                <AddIcon style={{ color: '#E91E63' }} />
              </QuantityButton>
            </QuantityControls>
          </QuantityContainer>
        )}

        {/* A total is only honest when every part of it has a price. */}
        {!hasNoOrderablePrice && (
          <TotalSection>
            <TotalLabel>Total</TotalLabel>
            <TotalPrice>{formatCurrency(calculateTotalPrice())}</TotalPrice>
          </TotalSection>
        )}

        {/* Delivery Zone Warning */}
        {isOutsideDeliveryZone && (
          <DeliveryWarning>
            <LocationOffIcon />
            <div>
              <strong>Outside Delivery Zone</strong>
              <p>
                This vendor doesn't deliver to your location
                {coverageKm ? ` (${coverageKm} km away by road` : ''}
                {coverageKm && coverage.radius ? `, max ${coverage.radius} km)` : coverageKm ? ')' : ''}
              </p>
            </div>
          </DeliveryWarning>
        )}

        {quoteOnly ? (
          <QuoteNotice>
            <RequestQuoteIcon />
            <div>
              <strong>This item is priced on request</strong>
              <p>
                {vendorName} quotes this one directly. Contact them with the
                quantity you need and they will confirm the price.
              </p>
              <QuoteActions>
                {quoteWhatsAppHref && (
                  <QuoteAction
                    href={quoteWhatsAppHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsAppIcon fontSize="small" /> WhatsApp the pharmacy
                  </QuoteAction>
                )}
                {vendorPhone && (
                  <QuoteAction href={`tel:${vendorPhone}`}>
                    <PhoneIcon fontSize="small" /> {vendorPhone}
                  </QuoteAction>
                )}
                {vendorEmail && (
                  <QuoteAction
                    href={`mailto:${vendorEmail}?subject=${encodeURIComponent(
                      `Quote request: ${product.name}`
                    )}&body=${encodeURIComponent(quoteEnquiryText)}`}
                  >
                    <EmailIcon fontSize="small" /> {vendorEmail}
                  </QuoteAction>
                )}
                {!quoteWhatsAppHref && !vendorPhone && !vendorEmail && (
                  <QuoteFallback>
                    {/* The helpline is per market (empty in ZM), so it is read
                        from the market config rather than typed in here. */}
                    This pharmacy has not published contact details yet.
                    {SUPPORT_PHONE
                      ? ` Call the Lusuku helpline on ${SUPPORT_PHONE} and we will put you in touch.`
                      : ' Contact Lusuku support and we will put you in touch.'}
                  </QuoteFallback>
                )}
              </QuoteActions>
            </div>
          </QuoteNotice>
        ) : (
          <>
          {branchBlocked && (
            <BranchAvailabilityBadge
              product={product}
              branchName={branchName}
              locations={locations}
              vendorName={vendor?.tradingName || vendor?.name || ''}
              onGetItFrom={onGetItFrom}
              accent="#E91E63"
            />
          )}
          <AddToCartButton
            onClick={handleAddToCart}
            disabled={!canAddToCart()}
            startIcon={<AddIcon />}
          >
            {unpriced
              ? 'No price yet, cannot be ordered'
              : branchBlocked
                ? addButtonLabel(product)
                : isOutsideDeliveryZone
                  ? 'Cannot Deliver to Your Location'
                  : 'Add to Cart'}
          </AddToCartButton>
          </>
        )}
      </DialogContentStyled>
      <OutletSwitchDialog
        open={Boolean(pendingMove)}
        fromLabel={cartBranchName}
        toLabel={branchName}
        onCancel={() => setPendingMove(null)}
        onConfirm={() => {
          const payload = pendingMove;
          setPendingMove(null);
          if (payload) applyAdd(payload, { moved: true });
        }}
      />
    </Dialog>
  );
};

export default HealthcareProductDialog;

// Styled Components
const DialogTitleStyled = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px !important;
`;

const DialogContentStyled = styled(DialogContent)`
  padding: 24px !important;

  @media (max-width: 768px) {
    padding: 16px !important;
  }
`;

/* Just a wrapper now. The frame, the height and the rounding moved into the
   shared gallery's own stage, and a fixed height here would have clipped the
   thumbnail rail the moment a pharmacy uploaded a second angle. */
const ProductImageContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const AlertBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  background-color: ${(props) =>
    props.severity === 'error' ? '#ffebee' : '#fff3e0'};
  color: ${(props) => (props.severity === 'error' ? '#d32f2f' : '#f57c00')};
  border: 1px solid ${(props) =>
    props.severity === 'error' ? '#f44336' : '#ff9800'};
`;

const ServiceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  background-color: #f3e5f5;
  color: #E91E63;
  border: 1px solid #E91E63;
`;

const DescriptionSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  margin: 0 0 8px 0;
  color: #333;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
`;

const PriceSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const PriceLabel = styled.span`
  font-size: 1rem;
  color: #666;
`;

const Price = styled.span`
  font-size: 1rem;
  font-weight: 400;
  color: #E91E63;
`;

const StockInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: ${(props) => (props.inStock ? '#e8f5e9' : '#ffebee')};
  color: ${(props) => (props.inStock ? '#4caf50' : '#d32f2f')};
  font-size: 0.9rem;
  font-weight: 500;
`;

const OptionsSection = styled.div`
  margin: 20px 0;
`;

const OptionGroup = styled.div`
  margin-bottom: 24px;
`;

const OptionGroupHeader = styled.div`
  margin-bottom: 12px;
`;

const OptionGroupTitle = styled.h4`
  font-size: 1rem;
  margin: 0 0 4px 0;
  color: #333;
`;

const RequiredLabel = styled.span`
  color: #d32f2f;
  font-size: 0.85rem;
  margin-left: 6px;
`;

const OptionGroupSubtitle = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  background: ${(props) => (props.selected ? '#f3e5f5' : '#fff')};
  border: 2px solid ${(props) => (props.selected ? '#E91E63' : '#e0e0e0')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #fce4ec;
    border-color: #E91E63;
  }
`;

const OptionContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const OptionName = styled.span`
  font-size: 0.95rem;
  color: #333;
`;

const OptionPrice = styled.span`
  font-size: 0.9rem;
  color: #E91E63;
  font-weight: 600;
  margin-left: 12px;
`;

const QuantityContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const QuantityLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuantityButton = styled(IconButton)`
  border: 2px solid #E91E63 !important;
  width: 36px !important;
  height: 36px !important;
  
  &:disabled {
    border-color: #e0e0e0 !important;
    opacity: 0.5;
  }
`;

const QuantityDisplay = styled(Typography)`
  font-size: 1.2rem !important;
  font-weight: 700 !important;
  color: #333;
  min-width: 40px;
  text-align: center;
`;

const TotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #fce4ec;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const TotalLabel = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
`;

const TotalPrice = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #E91E63;
`;

const AddToCartButton = styled(Button)`
  width: 100%;
  text-transform: none !important;
  background-color: #E91E63 !important;
  color: #fff !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  padding: 14px 24px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(233, 30, 99, 0.3) !important;
  transition: all 0.3s ease !important;
  
  &:disabled {
    background-color: #e0e0e0 !important;
    color: #9e9e9e !important;
    box-shadow: none !important;
  }
  
  &:hover:enabled {
    background-color: #C2185B !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(233, 30, 99, 0.4) !important;
  }
`;

/* Modelled on DeliveryWarning below, in the informative blue rather than the
   refusal red: a price on request is not an error, it is how this line is sold. */
const QuoteNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%);
  border: 1px solid #9fa8da;
  border-radius: 12px;

  svg {
    color: #3949ab;
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    flex: 1;
  }

  strong {
    display: block;
    color: #1a237e;
    font-size: 0.95rem;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    color: #303f9f;
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const QuoteActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const QuoteAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background-color: #3949ab;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;

  svg {
    color: #fff;
    font-size: 18px;
    margin-top: 0;
  }

  &:hover {
    background-color: #283593;
  }
`;

const QuoteFallback = styled.span`
  font-size: 0.8rem;
  color: #303f9f;
  line-height: 1.4;
`;

const DeliveryWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border: 1px solid #ef9a9a;
  border-radius: 12px;
  margin-bottom: 16px;

  svg {
    color: #c62828;
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    flex: 1;
  }

  strong {
    color: #c62828;
    font-size: 0.95rem;
    display: block;
    margin-bottom: 4px;
  }

  p {
    color: #b71c1c;
    font-size: 0.85rem;
    margin: 0;
    line-height: 1.4;
  }
`;
